import React, { useState } from "react";
import { graphql, Link } from "gatsby";
import { Context } from "../components/GlobalContext";
import Img from "gatsby-image";
import styled from "@emotion/styled";
import ReactPlayer from "react-player";
import Icon from "react-eva-icons";
import { css } from "@emotion/react";
import { Helmet } from "react-helmet";
import { v4 as uuidv4 } from "uuid";

const SiteContainer = styled.div`
  display: flex;
  align-items: stretch;
  align-content: stretch;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  @media screen and (max-width: 980px) {
    flex-direction: column;
  }
`;

const LeftHalf = styled.div`
  flex-grow: 0.3;
  flex-shrink: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: clamp(10rem, 50vh, 25rem);
  &::-webkit-scrollbar {
    width: 0px;
  }
  @media screen and (max-width: 1400px) {
    flex-grow: 1;
  }
  @media screen and (max-width: 980px) {
    border-bottom: 1px solid black;
    flex-shrink: 1;
  }
`;

const RightHalf = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  border-left: 1px solid black;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  @media screen and (max-width: 980px) {
    border-left: 0px;
  }
  &::-webkit-scrollbar {
    width: 1rem;
  }
  &::-webkit-scrollbar-track {
    border-left: 1px solid black;
  }
  &::-webkit-scrollbar-thumb {
    background-color: black;
  }
`;

const HomeButtonContainer = styled.div`
  height: 3.3rem;
  position: sticky;
  top: 0;
  background: white;
  border-bottom: 1px solid black;
  padding: 1rem 1.333rem;
`;

const WorkshopTextContainer = styled.article`
  margin-top: 10rem;
  padding: 1rem 1.333rem;
  height: max-content;
  margin-bottom: 10rem;
  @media screen and (max-width: 980px) {
    margin-top: 5rem;
  }
`;

const WorkTitle = styled.h1`
  margin-bottom: 0;
`;

const WorkSubtitle = styled.h2`
  margin-top: 0;
`;

const WorkTextLabel = styled.h3`
  margin-top: 2.4rem;
  margin-bottom: 0.6rem;
`;

const WorkTextInfo = styled.p`
  margin: 0;
`;

const WorkText = styled.div`
  margin-top: 3.6rem;
  margin-bottom: 3.6rem;
  max-width: 30rem;
`;

const NumberContainer = styled.div`
  width: 100%;
  height: clamp(10rem, 30vh, 25rem);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-size: clamp(10rem, 20vh, 25rem);
  padding-left: 5rem;
  padding-bottom: 1rem;
  color: var(--blue);
  user-select: none;
  position: sticky;
  top: 0;
  pointer-events: none;
  z-index: 100;
  border-bottom: 1px solid black;
  mix-blend-mode: multiply;
  @media screen and (max-width: 980px) {
    position: fixed;
    flex-direction: column;
    align-items: flex-end;
    top: 0rem;
    right: 1.4rem;
    height: auto;
    font-size: 6rem;
    padding-left: 0;
    padding-right: 0;
    border-bottom: 0;
  }
`;

const ContentWriting = styled.div`
  max-width: 20rem;
  padding: 0.8em 1rem 3em 1em;
  padding-right: 1em;
  p {
    &:first-of-type {
      margin-top: 0;
    }
    font-size: 0.8rem;
    a {
      text-decoration: underline;
      font-size: 0.8rem;
      color: var(--blue);
    }
  }
  @media screen and (max-width: 980px) {
    padding-right: 1em;
    padding-bottom: 2em;
    max-width: 20rem;
  }
  @media screen and (max-width: 600px) {
    max-width: initial;
    padding-bottom: 0em;
  }
`;

const IconContainer = styled.div`
  background: white;
  position: absolute;
  top: 0;
  left: 0;
  padding: 1rem;
  z-index: 100;
`;

const DocumentContainer = styled.div`
  margin-left: 1em;
  margin-top: 1em;
  margin-right: 0.5em;
  margin-bottom: 0.5em;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.1);
  @media screen and (max-width: 980px) {
    margin-right: 1em;
    margin-bottom: 0.5em;
  }
`;

const DocumentLink = styled.a`
  @media screen and (max-width: 600px) {
    max-width: calc(100vw - 3em);
  }
`;

const DocumentCaption = styled.div`
  padding-left: 0.9rem;
  padding-right: 1rem;
  p {
    font-size: 0.8rem;
    max-width: 16rem;
    a {
      text-decoration: underline;
      font-size: 0.8rem;
      color: var(--blue);
    }
  }
`;

const VideoContainer = styled.div`
  width: 500px;
  margin-top: 1em;
  margin-left: 1em;
  margin-right: 0.5em;
  margin-bottom: 0.5em;
`;

const PlayerWrapper = styled.div`
  width: 100%;
  height: 0;
  padding-top: 56.25%;
  position: relative;
  .react-player {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  @media screen and (max-width: 980px) {
    margin-right: 0em;
    margin-bottom: 0em;
  }
`;

const DownloadLink = styled.a`
  display: flex;
  i {
    margin-left: 0.2rem;
  }
  &:hover {
    svg {
      fill: var(--blue);
    }
  }
`;

const ExternalLink = styled.a`
  display: flex;
  i {
    margin-left: 0.3rem;
  }
  &:hover {
    svg {
      fill: var(--blue);
    }
  }
`;

const Footer = styled.div`
  width: 100%;
  height: 20rem;
  margin-top: 10rem;
  padding: 0 1rem;
  border-top: 1px solid black;
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: 1560px) {
    flex-direction: column;
  }
`;

const PreviousLink = styled(Link)`
  padding-right: 2rem;
  max-width: 30rem;
  flex-grow: 1;
  max-width: 25rem;
  margin-bottom: 2rem;
`;
const NextLink = styled(Link)`
  padding-left: 2rem;
  text-align: right;
  flex-grow: 1;
  max-width: 25rem;
  @media screen and (max-width: 1560px) {
    align-self: flex-end;
  }
`;

const LinkNumber = styled.span`
  font-size: 8rem;
  line-height: 0.85;
  margin: 0;
`;

const LinkInfoContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

export default function Template({ data, pageContext }) {
  const { next, previous } = pageContext;
  const { markdownRemark, arenaInnerChannel } = data; // data.markdownRemark holds your post data
  const { frontmatter, html } = markdownRemark;
  const { children } = arenaInnerChannel;
  const [workshopContent, setWorkshopContent] = useState(
    children
      .slice(0)
      .reverse()
      .map((content) => {
        if (content.content_html) {
          return (
            <ContentWriting
              key={content.id}
              dangerouslySetInnerHTML={{ __html: content.content_html }}
            ></ContentWriting>
          );
        } else if (
          content.source?.url.includes("vimeo") ||
          content.source?.url.includes("youtube")
        ) {
          return (
            <VideoContainer key={content.id}>
              <PlayerWrapper key={uuidv4()}>
                <ReactPlayer
                  key={uuidv4()}
                  className='react-player'
                  url={content.source.url}
                  width='100%'
                  height='100%'
                />
              </PlayerWrapper>
            </VideoContainer>
          );
        } else if (content.attachment && content.image) {
          return (
            <DocumentContainer key={content.id}>
              <DocumentLink
                key={uuidv4()}
                href={content.attachment.url}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  width:
                    content.image.childImageSharp.fluid.presentationWidth / 3,
                  display: "inline-block",
                }}
              >
                {/* <IconContainer>
                  <Icon
                    name='attach-2-outline'
                    size='xlarge'
                    fill='var(--blue)'
                    animation={{ type: "flip" }}
                  />
                </IconContainer> */}
                <Img
                  key={uuidv4()}
                  fluid={content.image.childImageSharp.fluid}
                />
              </DocumentLink>
              {content.description_html && (
                <DocumentCaption
                  key={uuidv4()}
                  dangerouslySetInnerHTML={{ __html: content.description_html }}
                />
              )}
            </DocumentContainer>
          );
        } else if (content.attachment && !content.image) {
          return (
            <DocumentContainer key={content.id}>
              <DocumentLink
                key={uuidv4()}
                href={content.attachment.url}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  display: "inline-block",
                }}
              >
                {/* <IconContainer>
                  <Icon
                    name='attach-2-outline'
                    size='xlarge'
                    fill='var(--blue)'
                    animation={{ type: "flip" }}
                  />
                </IconContainer> */}
                <DocumentCaption key={uuidv4()} style={{ paddingTop: "0.8em" }}>
                  {content.title}
                </DocumentCaption>
                {content.description_html && (
                  <DocumentCaption
                    key={uuidv4()}
                    dangerouslySetInnerHTML={{
                      __html: content.description_html,
                    }}
                  />
                )}
              </DocumentLink>
            </DocumentContainer>
          );
        } else if (content.image) {
          return (
            <DocumentContainer
              key={content.id}
              style={{
                width:
                  content.image.childImageSharp.fluid.presentationWidth / 3,
                display: "inline-block",
              }}
            >
              <DocumentLink
                key={uuidv4()}
                href={content.image.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <Img
                  key={uuidv4()}
                  fluid={content.image.childImageSharp.fluid}
                />
                {content.description_html && (
                  <DocumentCaption
                    key={uuidv4()}
                    dangerouslySetInnerHTML={{
                      __html: content.description_html,
                    }}
                  />
                )}
              </DocumentLink>
            </DocumentContainer>
          );
        }
      })
  );
  return (
    <SiteContainer>
      <Helmet
        title={`${data.site.siteMetadata.title} — ${frontmatter.title}`}
        defer={false}
      />
      <LeftHalf>
        <HomeButtonContainer>
          <Link to='/'>{data.site.siteMetadata.title}</Link>
        </HomeButtonContainer>
        <WorkshopTextContainer>
          {frontmatter.title && <WorkTitle>{frontmatter.title}</WorkTitle>}
          {frontmatter.subtitle && (
            <WorkSubtitle>{frontmatter.subtitle}</WorkSubtitle>
          )}
          {frontmatter.course_leader && (
            <WorkTextLabel>Course Leader</WorkTextLabel>
          )}
          {frontmatter.course_leader.map((leader, i) => {
            return <WorkTextInfo key={i}>{leader}</WorkTextInfo>;
          })}
          {frontmatter.teaching_assistant && (
            <WorkTextLabel>Teaching Assistants</WorkTextLabel>
          )}
          {frontmatter.teaching_assistant?.map((assistant, i) => {
            return <WorkTextInfo key={i}>{assistant}</WorkTextInfo>;
          })}
          {frontmatter.guests && <WorkTextLabel>Guests</WorkTextLabel>}
          {frontmatter.guests?.map((guest, i) => {
            return <WorkTextInfo key={i}>{guest}</WorkTextInfo>;
          })}
          {frontmatter.downloads.length > 0 && (
            <WorkTextLabel>Downloads</WorkTextLabel>
          )}
          {frontmatter.downloads?.map((download, i) => {
            return (
              <DownloadLink key={i} href={download.linkUrl.publicURL}>
                {download.linkText}
                <Icon
                  name='attach-2-outline'
                  size='large'
                  fill='black'
                  animation={{ type: "flip" }}
                />
              </DownloadLink>
            );
          })}
          {frontmatter.external.length > 0 && (
            <WorkTextLabel>External Links</WorkTextLabel>
          )}
          {frontmatter.external?.map((external, i) => {
            return (
              <ExternalLink
                key={i}
                href={external.linkUrl}
                target='_blank'
                rel='noopener noreferrer'
              >
                {external.linkText}
                <Icon
                  name='external-link-outline'
                  size='large'
                  fill='black'
                  animation={{ type: "flip" }}
                />
              </ExternalLink>
            );
          })}
          <WorkText dangerouslySetInnerHTML={{ __html: html }} />
          {frontmatter.students && <WorkTextLabel>Students</WorkTextLabel>}
          {frontmatter.students?.map((student, i) => {
            return <WorkTextInfo key={i}>{student}</WorkTextInfo>;
          })}
        </WorkshopTextContainer>
      </LeftHalf>
      <RightHalf>
        <Context.Consumer>
          {(context) => {
            return (
              <>
                <NumberContainer>{frontmatter.index}</NumberContainer>
              </>
            );
          }}
        </Context.Consumer>
        {workshopContent}
        <Footer>
          {previous ? (
            <PreviousLink to={previous.slug}>
              <h2>Previous Workshop</h2>
              <LinkInfoContainer>
                <LinkNumber style={{ marginRight: "1rem" }}>
                  {previous.index}
                </LinkNumber>
                <div>
                  {previous.course_leader.map((leader, i) => {
                    return <WorkTextInfo key={i}>{leader}</WorkTextInfo>;
                  })}
                  <WorkTextInfo>
                    <em>{previous.title}</em>
                  </WorkTextInfo>
                  {previous.subtitle && (
                    <WorkTextInfo>
                      <em>{previous.subtitle}</em>
                    </WorkTextInfo>
                  )}
                </div>
              </LinkInfoContainer>
            </PreviousLink>
          ) : (
            <div></div>
          )}
          {next ? (
            <NextLink to={next.slug}>
              <h2>Next Workshop</h2>
              <LinkInfoContainer style={{ flexDirection: "row-reverse" }}>
                <LinkNumber style={{ marginLeft: "1rem" }}>
                  {next.index}
                </LinkNumber>
                <div>
                  {next.course_leader.map((leader, i) => {
                    return <WorkTextInfo key={i}>{leader}</WorkTextInfo>;
                  })}
                  <WorkTextInfo>
                    <em>{next.title}</em>
                  </WorkTextInfo>
                  {next.subtitle && (
                    <WorkTextInfo>
                      <em>{next.subtitle}</em>
                    </WorkTextInfo>
                  )}
                </div>
              </LinkInfoContainer>
            </NextLink>
          ) : (
            <div></div>
          )}
        </Footer>
      </RightHalf>
    </SiteContainer>
  );
}

export const pageQuery = graphql`
  query($slug: String!, $arenaTitle: String!) {
    site {
      siteMetadata {
        title
      }
    }
    markdownRemark(frontmatter: { slug: { eq: $slug } }) {
      html
      frontmatter {
        date(formatString: "MMMM DD, YYYY")
        slug
        index
        title
        subtitle
        course_leader
        teaching_assistant
        guests
        students
        external {
          linkText
          linkUrl
        }
        downloads {
          linkText
          linkUrl {
            publicURL
          }
        }
      }
    }
    arenaInnerChannel(title: { eq: $arenaTitle }) {
      children {
        ... on ArenaBlock {
          id
          title
          description_html
          content_html
          image {
            url
            childImageSharp {
              fluid(maxWidth: 900) {
                ...GatsbyImageSharpFluid_noBase64
                presentationHeight
                presentationWidth
              }
            }
          }
          attachment {
            url
          }
          source {
            url
          }
        }
      }
    }
  }
`;
